import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarRange, Coins, Droplets, ExternalLink, Fuel, Gauge, Landmark, LayoutGrid, Paperclip, Pencil, Plus, ShieldCheck, Trash2, Wrench } from 'lucide-react';
import { Button, PageHeader, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type EcheanceDTO,
  type EcheanceType,
  type EnginDTO,
  type EnginStatut,
  type MaintenanceDTO,
  type MaintenanceStatut,
  type MaintenanceType,
  type PlanDTO,
  type PlanDeclencheur,
  type PlanStatut,
  type PleinCarburantDTO,
  type ReleveDTO,
  CATEGORIE_LABEL,
  ECHEANCE_LABEL,
  ECHEANCE_TYPES,
  addAffectation,
  addEcheance,
  addMaintenance,
  addPlan,
  addPlein,
  addReleve,
  cloturerMaintenance,
  deleteEcheance,
  deleteMaintenance,
  deletePlan,
  deletePlein,
  deleteReleve,
  getCarburantSynthese,
  getEngin,
  immobiliserEngin,
  getSyntheseEngin,
  listAffectations,
  listEcheances,
  listMaintenances,
  listPlans,
  listPleins,
  listReleves,
  updateEcheance,
  updateMaintenance,
  updatePlan,
  updatePlein,
  updateReleve,
} from '@/services/engins';
import { listChantiers } from '@/services/chantiers';
import { getEntreprise } from '@/services/parametres';
import { listTiers } from '@/services/tiers';
import { uploadFile } from '@/services/uploads';
import { toast } from '@/store/toastStore';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';

const STATUT_UI: Record<EnginStatut, { label: string; status: StatusKey }> = {
  DISPONIBLE: { label: 'Disponible', status: 'done' },
  AFFECTE: { label: 'Affecté', status: 'working' },
  MAINTENANCE: { label: 'Maintenance', status: 'review' },
  HORS_SERVICE: { label: 'Hors service', status: 'stuck' },
};
const MAINT_TYPE_LABEL: Record<MaintenanceType, string> = { PREVENTIVE: 'Préventive', CORRECTIVE: 'Corrective' };
const MAINT_STATUT_UI: Record<MaintenanceStatut, { label: string; status: StatusKey }> = {
  PLANIFIEE: { label: 'Planifiée', status: 'pending' },
  EN_COURS: { label: 'En cours', status: 'working' },
  TERMINEE: { label: 'Terminée', status: 'done' },
};
const PLAN_STATUT_UI: Record<PlanStatut, { label: string; status: StatusKey }> = {
  EN_ATTENTE: { label: 'En attente de relevés', status: 'pending' },
  A_JOUR: { label: 'À jour', status: 'done' },
  PROCHE: { label: 'Proche', status: 'working' },
  DEPASSE: { label: 'Dépassé', status: 'stuck' },
};
const DECLENCHEUR_LABEL: Record<PlanDeclencheur, string> = { HEURES: 'Heures', DATE: 'Date', LES_DEUX: 'Heures + date' };
const today = () => new Date().toISOString().slice(0, 10);
const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';

export function EnginDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('apercu');
  const { data: engin, isLoading } = useQuery({ queryKey: ['engin', id], queryFn: () => getEngin(id), enabled: !!id });
  const qc = useQueryClient();
  const immoMut = useMutation({
    mutationFn: () => immobiliserEngin(id),
    onSuccess: (r) => { toast.success(r.detail); qc.invalidateQueries({ queryKey: ['engin', id] }); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  if (isLoading || !engin) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-48 w-full" /></div>;
  }
  const st = STATUT_UI[engin.statut];

  return (
    <>
      <PageHeader
        title={engin.designation}
        subtitle={`${engin.code} · ${CATEGORIE_LABEL[engin.categorie]}${engin.immatriculation ? ` · ${engin.immatriculation}` : ''}`}
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Parc', to: '/engins' }, { label: engin.code }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => navigate('/engins/locations')} title="Pointer et facturer les locations de cette machine">
              <Coins /> Location
            </Button>
            {engin.est_immobilise ? (
              <StatusBadge status="done" label="Immobilisé" />
            ) : (
              <Button
                variant="secondary"
                onClick={() => { if (window.confirm('Capitaliser cet engin au registre des immobilisations ? Il deviendra amortissable (dotations D 681 / C 28x). Idempotent.')) immoMut.mutate(); }}
                loading={immoMut.isPending}
              >
                <Landmark /> Immobiliser
              </Button>
            )}
            <StatusBadge status={st.status} label={st.label} variant="solid" />
          </div>
        }
      />
      <Tabs
        tabs={[
          { key: 'apercu', label: "Vue d'ensemble", icon: <LayoutGrid className="size-4" /> },
          { key: 'releves', label: 'Relevés', icon: <Fuel className="size-4" /> },
          { key: 'carburant', label: 'Carburant', icon: <Droplets className="size-4" /> },
          { key: 'maintenances', label: 'Maintenances', icon: <Wrench className="size-4" /> },
          { key: 'echeances', label: 'Assurance & échéances', icon: <ShieldCheck className="size-4" /> },
          { key: 'affectations', label: 'Affectations', icon: <CalendarRange className="size-4" /> },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-6"
      />
      {tab === 'apercu' && <ApercuTab engin={engin} />}
      {tab === 'releves' && <RelevesTab engin={engin} />}
      {tab === 'carburant' && <CarburantTab engin={engin} />}
      {tab === 'maintenances' && <MaintenancesTab engin={engin} />}
      {tab === 'echeances' && <EcheancesTab enginId={id} />}
      {tab === 'affectations' && <AffectationsTab enginId={id} />}

    </>
  );
}

function ApercuTab({ engin }: { engin: EnginDTO }) {
  const { data: synthese } = useQuery({ queryKey: ['engin-synthese', engin.id], queryFn: () => getSyntheseEngin(engin.id) });
  const { data: tiers } = useQuery({ queryKey: ['tiers', 'all'], queryFn: () => listTiers({ size: 200 }), enabled: engin.type === 'LOUE' });
  const loueur = engin.loueur_id ? tiers?.items.find((t) => t.id === engin.loueur_id)?.raison_sociale : null;

  const specs: [string, string][] = [
    ['Marque / modèle', [engin.marque, engin.modele].filter(Boolean).join(' ') || '—'],
    ['Immatriculation', engin.immatriculation ?? '—'],
    ['Année', engin.annee_fabrication ? String(engin.annee_fabrication) : '—'],
    ['Type', engin.type === 'PROPRE' ? 'Propre' : 'Loué'],
    ['Compteur', `${Number(engin.compteur_actuel).toLocaleString('fr-DZ')} ${engin.unite_compteur}`],
    ['Prochaine révision', engin.prochaine_maintenance_date ?? (engin.prochaine_maintenance_km ? `${engin.prochaine_maintenance_km} ${engin.unite_compteur}` : '—')],
  ];

  // Honnêteté d'affichage : « — » quand la valeur n'est pas calculable (None), jamais « 0 ».
  const dz = (v: number | null) => (v == null ? '—' : formatDZD(v));
  const conso = synthese?.consommation_lh ?? null;
  const km = engin.unite_compteur === 'KM';
  const cards: { label: string; value: string; tone?: 'done' | 'stuck' }[] = synthese ? [
    { label: km ? 'Distance parcourue' : 'Heures de marche', value: `${Number(synthese.heures_marche).toLocaleString('fr-DZ')} ${km ? 'km' : 'h'}` },
    // Le taux d'utilisation (marche / (marche + arrêt)) n'a de sens qu'en heures.
    ...(km ? [] : [{ label: "Taux d'utilisation", value: synthese.taux_utilisation == null ? '—' : `${synthese.taux_utilisation} %` }]),
    { label: 'Carburant consommé', value: `${Number(synthese.carburant_litres).toLocaleString('fr-DZ')} L` },
    { label: 'Consommation moyenne', value: conso == null ? '—' : `${Number(conso).toFixed(1)} ${synthese.consommation_unite ?? ''}`.trim(), tone: synthese.surconsommation ? 'stuck' : undefined },
    { label: 'Coût carburant', value: dz(synthese.carburant_cout) },
    { label: 'Coût maintenance', value: dz(synthese.cout_maintenance), tone: synthese.cout_maintenance > 0 ? 'stuck' : undefined },
    // Loyer (engins loués) : Σ brut des décomptes validés — désormais inclus dans le coût de revient.
    ...(engin.type === 'LOUE' ? [{ label: 'Coût location (loyer)', value: dz(synthese.cout_location), tone: (synthese.cout_location > 0 ? 'stuck' : undefined) as 'stuck' | undefined }] : []),
    { label: km ? 'Coût de revient au km' : 'Coût de revient horaire', value: dz(synthese.cout_revient_horaire) },
  ] : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm lg:col-span-2">
          <h2 className="mb-4 text-base font-bold text-content-primary">Caractéristiques</h2>
          <dl className="grid grid-cols-2 gap-4">
            {specs.map(([k, v]) => (
              <div key={k}><dt className="label-caps">{k}</dt><dd className="mt-0.5 text-sm font-medium text-content-primary">{v}</dd></div>
            ))}
          </dl>
        </div>
        <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2"><Gauge className="size-4 text-brand-primary" /><h2 className="text-base font-bold text-content-primary">{engin.type === 'LOUE' ? 'Location' : 'Amortissement'}</h2></div>
          {engin.type === 'LOUE' ? (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-content-secondary">Loueur</dt><dd className="font-semibold">{loueur ?? '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-content-secondary">Coût / jour</dt><dd className="font-semibold tabular-nums">{engin.cout_location_jour != null ? formatDZD(engin.cout_location_jour) : '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-content-secondary">Début</dt><dd className="tabular-nums">{engin.location_date_debut ?? '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-content-secondary">Fin</dt><dd className="tabular-nums">{engin.location_date_fin ?? '—'}</dd></div>
            </dl>
          ) : (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-content-secondary">Valeur d'acquisition</dt><dd className="font-semibold tabular-nums">{engin.valeur_acquisition != null ? formatDZD(engin.valeur_acquisition) : '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-content-secondary">Durée amort.</dt><dd className="font-semibold">{engin.duree_amortissement_ans ? `${engin.duree_amortissement_ans} ans` : '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-content-secondary">Amort. annuel</dt><dd className="font-semibold tabular-nums">{synthese ? dz(synthese.amortissement_annuel) : '—'}</dd></div>
              <div className="flex justify-between border-t border-border pt-2"><dt className="font-bold text-content-primary">Valeur résiduelle</dt><dd className="font-bold tabular-nums text-brand-primary">{synthese ? dz(synthese.valeur_residuelle) : '—'}</dd></div>
            </dl>
          )}
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-base font-bold text-content-primary">Synthèse des coûts & rentabilité</h2>
        {synthese?.surconsommation && synthese.consommation_lh != null && synthese.conso_reference_lh != null && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-status-stuck/30 bg-status-stuck/10 px-4 py-2.5 text-sm text-status-stuck">
            <AlertTriangle className="size-4" /> Surconsommation : {Number(synthese.consommation_lh).toFixed(1)} L/h pour une référence de {Number(synthese.conso_reference_lh).toFixed(1)} L/h (seuil +30 %).
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => (
            <div key={c.label} className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
              <p className="label-caps">{c.label}</p>
              <p className={cn('mt-1 text-2xl font-bold tabular-nums', c.tone === 'stuck' ? 'text-status-stuck' : 'text-content-primary')}>{c.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <div className="flex min-h-[24vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center"><p className="px-6 text-sm text-content-secondary">{message}</p></div>;
}

/** Actions de ligne (modifier / supprimer) — cohérentes avec le reste de l'ERP. */
function RowActions({ onEdit, onDelete }: { onEdit?: () => void; onDelete?: () => void }) {
  return (
    <div className="flex items-center justify-end gap-1">
      {onEdit && <button type="button" onClick={onEdit} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-content-primary" aria-label="Modifier"><Pencil className="size-4" /></button>}
      {onDelete && <button type="button" onClick={onDelete} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>}
    </div>
  );
}

function RelevesTab({ engin }: { engin: EnginDTO }) {
  const enginId = engin.id;
  const km = engin.unite_compteur === 'KM';
  const queryClient = useQueryClient();
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ date: today(), heures_marche: '', heures_arret: '' });
  const { data, isLoading } = useQuery({ queryKey: ['releves', enginId], queryFn: () => listReleves(enginId) });
  const invalidate = () => { ['releves', 'engin', 'engin-synthese', 'plans'].forEach((k) => queryClient.invalidateQueries({ queryKey: [k, enginId] })); queryClient.invalidateQueries({ queryKey: ['engins-dashboard'] }); queryClient.invalidateQueries({ queryKey: ['notifications'] }); };
  const reset = () => { setEditId(null); setForm({ date: today(), heures_marche: '', heures_arret: '' }); };

  const saveMut = useMutation({
    mutationFn: () => {
      const p = { date: form.date, heures_marche: Number(form.heures_marche) || 0, heures_arret: Number(form.heures_arret) || 0 };
      return editId ? updateReleve(editId, p) : addReleve({ engin_id: enginId, ...p });
    },
    onSuccess: () => { invalidate(); toast.success(editId ? 'Relevé modifié, compteur recalculé.' : 'Relevé enregistré.'); reset(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({ mutationFn: (id: string) => deleteReleve(id), onSuccess: () => { invalidate(); toast.success('Relevé supprimé, compteur recalculé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const startEdit = (r: ReleveDTO) => { setEditId(r.id); setForm({ date: r.date, heures_marche: String(r.heures_marche), heures_arret: String(r.heures_arret) }); };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-1 text-base font-bold text-content-primary">{editId ? 'Modifier le relevé' : 'Nouveau relevé journalier'}</h2>
        <p className="mb-3 text-xs text-content-muted">{km ? <>Distance parcourue (km), relevée au quotidien. Elle alimente</> : <>Heures de marche / arrêt. Les heures de marche alimentent</>} le <strong>compteur de l'engin</strong> et le <strong>préventif aux {km ? 'kilomètres' : 'heures'}</strong>. Le carburant se saisit dans l'onglet <strong>Carburant</strong>.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div><label className="mb-1 block text-xs text-content-muted">Date</label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">{km ? 'Distance parcourue (km)' : 'Heures de marche'}</label><Input type="number" step="1" value={form.heures_marche} onChange={(e) => setForm({ ...form, heures_marche: e.target.value })} /></div>
          {!km && <div><label className="mb-1 block text-xs text-content-muted">Heures d'arrêt</label><Input type="number" step="1" value={form.heures_arret} onChange={(e) => setForm({ ...form, heures_arret: e.target.value })} /></div>}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          {editId && <Button variant="secondary" onClick={reset}>Annuler</Button>}
          <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending}><Plus /> {editId ? 'Enregistrer' : 'Ajouter le relevé'}</Button>
        </div>
      </div>
      {isLoading ? <Skeleton className="h-48 w-full" /> : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Date</th>
              <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">{km ? 'Distance (km)' : 'H. marche'}</th>
              {!km && <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">H. arrêt</th>}
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{r.date}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{km ? Number(r.heures_marche).toLocaleString('fr-DZ') : Number(r.heures_marche).toFixed(1)}</td>
                  {!km && <td className="px-4 py-2.5 text-right tabular-nums text-content-secondary">{Number(r.heures_arret).toFixed(1)}</td>}
                  <td className="px-4 py-2.5 text-right"><RowActions onEdit={() => startEdit(r)} onDelete={() => { if (window.confirm('Supprimer ce relevé ? Le compteur de l\'engin sera recalculé.')) delMut.mutate(r.id); }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState message={km ? 'Aucun relevé. Saisissez la distance parcourue au quotidien.' : 'Aucun relevé. Saisissez les heures de marche / arrêt au quotidien.'} />}
    </div>
  );
}

function CarburantTab({ engin }: { engin: EnginDTO }) {
  const queryClient = useQueryClient();
  const enginId = engin.id;
  const empty = { date_plein: today(), quantite_litres: '', prix_unitaire: '', montant: '', compteur_au_plein: '', station: '', reference_bon: '', chantier_id: '', justificatif_url: '' };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState<string | null>(null);
  const [dernier, setDernier] = useState<'litres' | 'montant'>('litres');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: entreprise } = useQuery({ queryKey: ['entreprise'], queryFn: getEntreprise });
  const prixDefaut = Number(entreprise?.prix_carburant_defaut ?? 0);
  const { data: pleins, isLoading } = useQuery({ queryKey: ['pleins', enginId], queryFn: () => listPleins(enginId) });
  const { data: synthese } = useQuery({ queryKey: ['carburant-synthese', enginId], queryFn: () => getCarburantSynthese(enginId) });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers', 'actifs'], queryFn: () => listChantiers() });
  const nomChantier = (cid: string | null) => (cid ? (chantiers?.items.find((c) => c.id === cid)?.nom ?? '—') : '—');

  // Prix unitaire PRÉ-REMPLI avec le prix gasoil par défaut (31), modifiable.
  useEffect(() => { if (prixDefaut && form.prix_unitaire === '') setForm((f) => ({ ...f, prix_unitaire: String(prixDefaut) })); }, [prixDefaut]); // eslint-disable-line react-hooks/exhaustive-deps
  const reset = () => { setEditId(null); setForm({ ...empty, prix_unitaire: prixDefaut ? String(prixDefaut) : '' }); };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['pleins', enginId] });
    queryClient.invalidateQueries({ queryKey: ['carburant-synthese', enginId] });
    queryClient.invalidateQueries({ queryKey: ['engin', enginId] });
    queryClient.invalidateQueries({ queryKey: ['engin-synthese', enginId] });
    queryClient.invalidateQueries({ queryKey: ['engins-dashboard'] });
  };

  // Calcul BIDIRECTIONNEL : le dernier champ saisi (litres OU montant) pilote l'autre via le PU.
  const pu = form.prix_unitaire !== '' ? Number(form.prix_unitaire) : prixDefaut;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const onLitres = (v: string) => { setDernier('litres'); setForm((f) => ({ ...f, quantite_litres: v, montant: v === '' ? '' : String(r2((Number(v) || 0) * (pu || 0))) })); };
  const onMontant = (v: string) => { setDernier('montant'); setForm((f) => ({ ...f, montant: v, quantite_litres: v === '' || pu <= 0 ? f.quantite_litres : String(r2((Number(v) || 0) / pu)) })); };
  const onPU = (v: string) => {
    const np = v !== '' ? Number(v) : prixDefaut;
    setForm((f) => {
      if (dernier === 'montant' && f.montant !== '' && np > 0) return { ...f, prix_unitaire: v, quantite_litres: String(r2((Number(f.montant) || 0) / np)) };
      if (f.quantite_litres !== '') return { ...f, prix_unitaire: v, montant: String(r2((Number(f.quantite_litres) || 0) * (np || 0))) };
      return { ...f, prix_unitaire: v };
    });
  };

  const handleBon = async (file: File) => {
    setUploading(true);
    try { const { url } = await uploadFile(file); setForm((f) => ({ ...f, justificatif_url: url })); toast.success('Bon joint — pensez à enregistrer le plein.'); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setUploading(false); }
  };

  const saveMut = useMutation({
    mutationFn: () => {
      const p = {
        date_plein: form.date_plein,
        quantite_litres: Number(form.quantite_litres) || 0,
        prix_unitaire: form.prix_unitaire ? Number(form.prix_unitaire) : undefined,
        montant: form.montant ? Number(form.montant) : null,
        compteur_au_plein: form.compteur_au_plein ? Number(form.compteur_au_plein) : null,
        station: form.station || null,
        reference_bon: form.reference_bon || null,
        justificatif_url: form.justificatif_url || null,
        chantier_id: form.chantier_id || null,
      };
      return editId ? updatePlein(enginId, editId, p) : addPlein(enginId, p);
    },
    onSuccess: () => { invalidate(); toast.success(editId ? 'Plein modifié.' : 'Plein enregistré.'); reset(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (pid: string) => deletePlein(enginId, pid),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const startEdit = (p: PleinCarburantDTO) => {
    setEditId(p.id);
    setForm({
      date_plein: p.date_plein, quantite_litres: String(p.quantite_litres), prix_unitaire: String(p.prix_unitaire),
      montant: String(p.montant), compteur_au_plein: p.compteur_au_plein != null ? String(p.compteur_au_plein) : '',
      station: p.station ?? '', reference_bon: p.reference_bon ?? '', chantier_id: p.chantier_id ?? '', justificatif_url: p.justificatif_url ?? '',
    });
  };

  const cards = synthese ? [
    { label: 'Pleins', value: String(synthese.nb_pleins) },
    { label: 'Litres totaux', value: `${Number(synthese.total_litres).toLocaleString('fr-DZ')} L` },
    { label: 'Coût carburant', value: formatDZD(synthese.total_cout) },
    { label: 'Conso moyenne', value: synthese.conso_moyenne != null ? `${Number(synthese.conso_moyenne).toFixed(1)} ${synthese.unite_consommation}` : '—' },
  ] : [];

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-1 text-base font-bold text-content-primary">{editId ? 'Modifier le plein' : 'Nouveau plein'}</h2>
        <p className="mb-3 text-xs text-content-muted">Saisissez les <strong>litres</strong> OU le <strong>montant</strong> : l'autre se calcule via le prix unitaire (pré-rempli à {prixDefaut || 31}, modifiable). Le compteur au plein sert à la consommation entre deux pleins ({engin.unite_compteur === 'KM' ? 'L/100 km' : 'L/h'}).</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div><label className="mb-1 block text-xs text-content-muted">Date</label><Input type="date" value={form.date_plein} onChange={(e) => setForm({ ...form, date_plein: e.target.value })} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">Litres</label><Input type="number" step="0.01" placeholder="Litres" value={form.quantite_litres} onChange={(e) => onLitres(e.target.value)} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">Montant (DZD)</label><Input type="number" step="0.01" placeholder="Montant" value={form.montant} onChange={(e) => onMontant(e.target.value)} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">Prix unitaire (DZD/L)</label><Input type="number" step="0.01" placeholder={String(prixDefaut || 31)} value={form.prix_unitaire} onChange={(e) => onPU(e.target.value)} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">Compteur au plein ({engin.unite_compteur})</label><Input type="number" step="1" placeholder="Compteur" value={form.compteur_au_plein} onChange={(e) => setForm({ ...form, compteur_au_plein: e.target.value })} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">Station</label><Input placeholder="Station / lieu" value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">N° de bon</label><Input placeholder="N° bon / ticket" value={form.reference_bon} onChange={(e) => setForm({ ...form, reference_bon: e.target.value })} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">Chantier (facultatif)</label>
            <select value={form.chantier_id} onChange={(e) => setForm({ ...form, chantier_id: e.target.value })} className={selectCls}>
              <option value="">—</option>
              {chantiers?.items.map((ch) => <option key={ch.id} value={ch.id}>{ch.code} · {ch.nom}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleBon(f); e.target.value = ''; }} />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} loading={uploading}><Paperclip className="size-4" /> {form.justificatif_url ? 'Bon joint ✓' : 'Joindre le bon (pdf/jpg/png)'}</Button>
            {form.justificatif_url && <button type="button" onClick={() => setForm((f) => ({ ...f, justificatif_url: '' }))} className="text-xs text-status-stuck hover:underline">retirer</button>}
          </div>
          <div className="flex gap-2">
            {editId && <Button variant="secondary" onClick={reset}>Annuler</Button>}
            <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!form.quantite_litres}><Plus /> {editId ? 'Enregistrer' : 'Enregistrer le plein'}</Button>
          </div>
        </div>
      </div>

      {cards.length > 0 && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c) => (
            <div key={c.label} className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
              <p className="label-caps">{c.label}</p>
              <p className="mt-1 text-xl font-bold tabular-nums text-content-primary">{c.value}</p>
            </div>
          ))}
        </div>
      )}
      {synthese && synthese.nb_surconsommations > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-status-stuck/30 bg-status-stuck/10 px-4 py-2.5 text-sm text-status-stuck">
          <AlertTriangle className="size-4" /> {synthese.nb_surconsommations} plein(s) en surconsommation (&gt; +30 % vs la moyenne) — à contrôler.
        </div>
      )}

      {isLoading ? <Skeleton className="h-48 w-full" /> : pleins && pleins.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Date</th>
              <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Compteur</th>
              <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Litres</th>
              <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Montant</th>
              <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Consommation</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Chantier</th>
              <th className="px-4 py-2.5 text-center font-semibold text-content-secondary">Bon</th>
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {pleins.map((p) => (
                <tr key={p.id} className={cn('border-b border-border last:border-0 hover:bg-surface-hover', p.surconsommation && 'bg-status-stuck/5')}>
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{p.date_plein}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{p.compteur_au_plein != null ? Number(p.compteur_au_plein).toLocaleString('fr-DZ') : '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{Number(p.quantite_litres).toFixed(0)} L</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatDZD(p.montant)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {p.consommation != null ? (
                      <span className={cn('inline-flex items-center justify-end gap-1 font-medium', p.surconsommation && 'text-status-stuck')}>
                        {p.surconsommation && <AlertTriangle className="size-3.5" />}
                        {Number(p.consommation).toFixed(1)} {p.unite_consommation}
                      </span>
                    ) : <span className="text-content-muted">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-content-secondary">{nomChantier(p.chantier_id)}</td>
                  <td className="px-4 py-2.5 text-center">{p.justificatif_url ? <a href={p.justificatif_url} target="_blank" rel="noreferrer" className="text-brand-primary hover:opacity-70" title="Ouvrir le bon"><ExternalLink className="inline size-4" /></a> : <span className="text-content-muted">—</span>}</td>
                  <td className="px-4 py-2.5 text-right"><RowActions onEdit={() => startEdit(p)} onDelete={() => { if (window.confirm('Supprimer ce plein ? Le coût et la consommation seront recalculés.')) delMut.mutate(p.id); }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState message="Aucun plein. Saisissez les pleins avec le compteur pour suivre la consommation et détecter les surconsommations." />}
    </div>
  );
}

function echeancePlan(p: PlanDTO): string {
  if (p.statut === 'EN_ATTENTE') return '—';
  const parts: string[] = [];
  if (p.seuil_heures != null) parts.push(`${Number(p.seuil_heures).toLocaleString('fr-DZ')} ${p.unite_compteur}`);
  if (p.seuil_date != null) parts.push(p.seuil_date);
  return parts.join(' · ') || '—';
}
function restePlan(p: PlanDTO): string {
  if (p.statut === 'EN_ATTENTE') return 'en attente de relevés';
  const parts: string[] = [];
  if (p.reste_heures != null) parts.push(`${Number(p.reste_heures).toLocaleString('fr-DZ')} ${p.unite_compteur}`);
  if (p.reste_jours != null) parts.push(`${p.reste_jours} j`);
  return parts.join(' · ') || '—';
}

function MaintenancesTab({ engin }: { engin: EnginDTO }) {
  const enginId = engin.id;
  const queryClient = useQueryClient();
  const emptyForm = { type: 'PREVENTIVE', date_debut: today(), description: '', cout_pieces: '', cout_main_oeuvre: '', compteur: '', plan_id: '', justificatif_url: '' };
  const [form, setForm] = useState(emptyForm);
  const [editMid, setEditMid] = useState<string | null>(null);
  const [planForm, setPlanForm] = useState({ libelle: '', declencheur: 'HEURES' as PlanDeclencheur, periodicite_heures: '', periodicite_mois: '' });
  const [editPid, setEditPid] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useQuery({ queryKey: ['maintenances', enginId], queryFn: () => listMaintenances(enginId) });
  const { data: plans } = useQuery({ queryKey: ['plans', enginId], queryFn: () => listPlans(enginId) });
  const invalidate = () => {
    ['maintenances', 'plans', 'engin', 'engin-synthese'].forEach((k) => queryClient.invalidateQueries({ queryKey: [k, enginId] }));
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    queryClient.invalidateQueries({ queryKey: ['engins-dashboard'] });
  };
  const resetMaint = () => { setEditMid(null); setForm(emptyForm); };
  const resetPlan = () => { setEditPid(null); setPlanForm({ libelle: '', declencheur: 'HEURES', periodicite_heures: '', periodicite_mois: '' }); };

  const handleFacture = async (file: File) => {
    setUploading(true);
    try { const { url } = await uploadFile(file); setForm((f) => ({ ...f, justificatif_url: url })); toast.success('Pièce jointe — pensez à enregistrer.'); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setUploading(false); }
  };

  const saveMaintMut = useMutation({
    mutationFn: () => {
      const p = {
        type: form.type as MaintenanceType, date_debut: form.date_debut, description: form.description || null,
        cout_pieces: Number(form.cout_pieces) || 0, cout_main_oeuvre: Number(form.cout_main_oeuvre) || 0,
        compteur: form.compteur ? Number(form.compteur) : null, plan_id: form.plan_id || null,
        justificatif_url: form.justificatif_url || null,
      };
      return editMid ? updateMaintenance(editMid, p) : addMaintenance({ engin_id: enginId, statut: 'EN_COURS', ...p });
    },
    onSuccess: () => { invalidate(); toast.success(editMid ? 'Intervention modifiée.' : 'Maintenance enregistrée.'); resetMaint(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMaintMut = useMutation({ mutationFn: (mid: string) => deleteMaintenance(mid), onSuccess: () => { invalidate(); toast.success('Intervention supprimée.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const cloturerMut = useMutation({
    mutationFn: (mid: string) => cloturerMaintenance(enginId, mid),
    onSuccess: () => { invalidate(); toast.success('Maintenance clôturée, engin disponible.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const startEditMaint = (m: MaintenanceDTO) => {
    setEditMid(m.id);
    setForm({
      type: m.type, date_debut: m.date_debut ?? today(), description: m.description ?? '',
      cout_pieces: String(m.cout_pieces), cout_main_oeuvre: String(m.cout_main_oeuvre),
      compteur: m.compteur != null ? String(m.compteur) : '', plan_id: m.plan_id ?? '', justificatif_url: m.justificatif_url ?? '',
    });
  };

  const savePlanMut = useMutation({
    mutationFn: () => {
      const p = {
        libelle: planForm.libelle, declencheur: planForm.declencheur,
        periodicite_heures: planForm.periodicite_heures ? Number(planForm.periodicite_heures) : null,
        periodicite_mois: planForm.periodicite_mois ? Number(planForm.periodicite_mois) : null,
      };
      return editPid ? updatePlan(enginId, editPid, p) : addPlan(enginId, p);
    },
    onSuccess: () => { invalidate(); toast.success(editPid ? 'Plan modifié.' : 'Plan préventif créé.'); resetPlan(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delPlanMut = useMutation({ mutationFn: (pid: string) => deletePlan(enginId, pid), onSuccess: () => invalidate(), onError: (e) => toast.error(getApiErrorMessage(e)) });
  const startEditPlan = (p: PlanDTO) => {
    setEditPid(p.id);
    setPlanForm({ libelle: p.libelle, declencheur: p.declencheur, periodicite_heures: p.periodicite_heures != null ? String(p.periodicite_heures) : '', periodicite_mois: p.periodicite_mois != null ? String(p.periodicite_mois) : '' });
  };

  const km = engin.unite_compteur === 'KM';
  const besoinH = planForm.declencheur !== 'DATE';
  const besoinM = planForm.declencheur !== 'HEURES';

  return (
    <div className="space-y-5">
      {/* Plans de maintenance préventive (aux heures / à la date) */}
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-1 text-base font-bold text-content-primary">{editPid ? 'Modifier le plan préventif' : 'Plans de maintenance préventive'}</h2>
        <p className="mb-3 text-xs text-content-muted">Déclenchement aux {km ? 'kilomètres' : 'heures'} (le compteur vient des relevés journaliers), à la date, ou aux deux. Le premier seuil atteint déclenche l'alerte.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input placeholder="Libellé (ex : vidange)" value={planForm.libelle} onChange={(e) => setPlanForm({ ...planForm, libelle: e.target.value })} />
          <select value={planForm.declencheur} onChange={(e) => setPlanForm({ ...planForm, declencheur: e.target.value as PlanDeclencheur })} className={selectCls}>
            <option value="HEURES">{km ? 'Aux kilomètres' : 'Aux heures'}</option>
            <option value="DATE">À la date</option>
            <option value="LES_DEUX">{km ? 'Km + date' : 'Heures + date'}</option>
          </select>
          <Input type="number" step="1" placeholder={`Tous les … ${km ? 'km' : 'h'}${besoinH ? ' *' : ''}`} disabled={!besoinH} value={planForm.periodicite_heures} onChange={(e) => setPlanForm({ ...planForm, periodicite_heures: e.target.value })} />
          <Input type="number" step="1" placeholder={`Tous les … mois${besoinM ? ' *' : ''}`} disabled={!besoinM} value={planForm.periodicite_mois} onChange={(e) => setPlanForm({ ...planForm, periodicite_mois: e.target.value })} />
        </div>
        <div className="mt-3 flex justify-end gap-2">
          {editPid && <Button variant="ghost" size="sm" onClick={resetPlan}>Annuler</Button>}
          <Button variant="secondary" size="sm" onClick={() => savePlanMut.mutate()} loading={savePlanMut.isPending} disabled={!planForm.libelle || (besoinH && !planForm.periodicite_heures) || (besoinM && !planForm.periodicite_mois)}><Plus /> {editPid ? 'Enregistrer' : 'Ajouter le plan'}</Button>
        </div>

        {plans && plans.length > 0 && (
          <div className="mt-4 overflow-x-auto rounded-md border border-border bg-surface-primary">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase text-content-muted">
                <th className="px-3 py-2">Libellé</th><th className="px-3 py-2">Déclencheur</th><th className="px-3 py-2">Périodicité</th>
                <th className="px-3 py-2">Prochaine échéance</th><th className="px-3 py-2">Reste</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2" />
              </tr></thead>
              <tbody>
                {plans.map((p) => {
                  const s = PLAN_STATUT_UI[p.statut];
                  return (
                    <tr key={p.id} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-1.5 font-medium text-content-primary">{p.libelle}</td>
                      <td className="px-3 py-1.5 text-content-secondary">{DECLENCHEUR_LABEL[p.declencheur]}</td>
                      <td className="px-3 py-1.5 tabular-nums text-content-secondary">{[p.periodicite_heures ? `${p.periodicite_heures} h` : null, p.periodicite_mois ? `${p.periodicite_mois} mois` : null].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="px-3 py-1.5 tabular-nums">{echeancePlan(p)}</td>
                      <td className="px-3 py-1.5 tabular-nums font-medium">{restePlan(p)}</td>
                      <td className="px-3 py-1.5"><StatusBadge status={s.status} label={s.label} /></td>
                      <td className="px-3 py-1.5 text-right"><RowActions onEdit={() => startEditPlan(p)} onDelete={() => { if (window.confirm(`Supprimer le plan « ${p.libelle} » ?`)) delPlanMut.mutate(p.id); }} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Intervention */}
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-3 text-base font-bold text-content-primary">{editMid ? "Modifier l'intervention" : 'Nouvelle intervention'}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={selectCls}>
            <option value="PREVENTIVE">Préventive</option>
            <option value="CORRECTIVE">Corrective</option>
          </select>
          <Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} />
          <Input type="number" step="1" placeholder="Coût pièces" value={form.cout_pieces} onChange={(e) => setForm({ ...form, cout_pieces: e.target.value })} />
          <Input type="number" step="1" placeholder="Coût main d'œuvre" value={form.cout_main_oeuvre} onChange={(e) => setForm({ ...form, cout_main_oeuvre: e.target.value })} />
          <Input type="number" step="1" placeholder={`Compteur (${engin.unite_compteur}) — défaut ${Number(engin.compteur_actuel).toLocaleString('fr-DZ')}`} value={form.compteur} onChange={(e) => setForm({ ...form, compteur: e.target.value })} className="lg:col-span-2" />
          <select value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} className={`${selectCls} lg:col-span-2`}>
            <option value="">Plan préventif à relancer (facultatif)</option>
            {plans?.map((p) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
          </select>
          <Input className="lg:col-span-4" placeholder="Description de l'intervention" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFacture(f); e.target.value = ''; }} />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} loading={uploading}><Paperclip className="size-4" /> {form.justificatif_url ? 'Facture jointe ✓' : 'Joindre la facture (pdf/jpg/png)'}</Button>
            {form.justificatif_url && <button type="button" onClick={() => setForm((f) => ({ ...f, justificatif_url: '' }))} className="text-xs text-status-stuck hover:underline">retirer</button>}
          </div>
          <div className="flex gap-2">
            {editMid && <Button variant="secondary" onClick={resetMaint}>Annuler</Button>}
            <Button onClick={() => saveMaintMut.mutate()} loading={saveMaintMut.isPending}><Plus /> {editMid ? 'Enregistrer' : 'Enregistrer'}</Button>
          </div>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-48 w-full" /> : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Date</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Type</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Description</th>
              <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Compteur</th>
              <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Coût total</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Statut</th>
              <th className="px-4 py-2.5 text-center font-semibold text-content-secondary">Pièce</th>
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {data.map((m) => {
                const s = MAINT_STATUT_UI[m.statut];
                return (
                  <tr key={m.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                    <td className="px-4 py-2.5 tabular-nums text-content-secondary">{m.date_debut ?? '—'}</td>
                    <td className="px-4 py-2.5">{MAINT_TYPE_LABEL[m.type]}</td>
                    <td className="px-4 py-2.5 text-content-secondary">{m.description ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-content-secondary">{m.compteur != null ? `${Number(m.compteur).toLocaleString('fr-DZ')} ${engin.unite_compteur}` : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums">{formatDZD(Number(m.cout_pieces) + Number(m.cout_main_oeuvre))}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={s.status} label={s.label} /></td>
                    <td className="px-4 py-2.5 text-center">{m.justificatif_url ? <a href={m.justificatif_url} target="_blank" rel="noreferrer" className="text-brand-primary hover:opacity-70" title="Ouvrir la facture"><ExternalLink className="inline size-4" /></a> : <span className="text-content-muted">—</span>}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {m.statut !== 'TERMINEE' && <Button variant="ghost" size="sm" onClick={() => cloturerMut.mutate(m.id)}>Clôturer</Button>}
                        <RowActions onEdit={() => startEditMaint(m)} onDelete={() => { if (window.confirm('Supprimer cette intervention ? Le coût maintenance sera recalculé.')) delMaintMut.mutate(m.id); }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <EmptyState message="Aucune maintenance enregistrée." />}
    </div>
  );
}

function EcheancesTab({ enginId }: { enginId: string }) {
  const queryClient = useQueryClient();
  const empty = { type: 'ASSURANCE', organisme: '', reference: '', date_debut: today(), date_fin: '', montant: '', document_url: '' };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useQuery({ queryKey: ['echeances', enginId], queryFn: () => listEcheances(enginId) });
  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['echeances', enginId] }); queryClient.invalidateQueries({ queryKey: ['notifications'] }); queryClient.invalidateQueries({ queryKey: ['engins-dashboard'] }); };
  const reset = () => { setEditId(null); setForm(empty); };

  const handleDoc = async (file: File) => {
    setUploading(true);
    try { const { url } = await uploadFile(file); setForm((f) => ({ ...f, document_url: url })); toast.success('Document joint — pensez à enregistrer.'); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setUploading(false); }
  };

  const saveMut = useMutation({
    mutationFn: () => {
      const p = {
        type: form.type as EcheanceType, organisme: form.organisme || null, reference: form.reference || null,
        date_debut: form.date_debut || null, date_fin: form.date_fin || null,
        montant: form.montant ? Number(form.montant) : null, document_url: form.document_url || null,
      };
      return editId ? updateEcheance(enginId, editId, p) : addEcheance(enginId, { ...p, notes: null });
    },
    onSuccess: () => { invalidate(); toast.success(editId ? 'Échéance modifiée.' : 'Échéance enregistrée.'); reset(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (eid: string) => deleteEcheance(enginId, eid),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const startEdit = (ech: EcheanceDTO) => {
    setEditId(ech.id);
    setForm({ type: ech.type, organisme: ech.organisme ?? '', reference: ech.reference ?? '', date_debut: ech.date_debut ?? today(), date_fin: ech.date_fin ?? '', montant: ech.montant != null ? String(ech.montant) : '', document_url: ech.document_url ?? '' });
  };

  const statutEcheance = (dateFin: string | null) => {
    if (!dateFin) return null;
    const j = Math.ceil((new Date(dateFin).getTime() - Date.now()) / 86400000);
    if (j < 0) return { label: `Expirée (${-j} j)`, status: 'stuck' as const };
    if (j <= 30) return { label: `Expire dans ${j} j`, status: 'working' as const };
    return { label: 'Valide', status: 'done' as const };
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-1 text-base font-bold text-content-primary">{editId ? "Modifier l'échéance" : 'Nouvelle échéance'}</h2>
        <p className="mb-3 text-xs text-content-muted">Assurance, contrôle technique, vignette… La date d'expiration alimente les rappels. Joignez le contrat / la police d'assurance.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={selectCls}>
            {ECHEANCE_TYPES.map((t) => <option key={t} value={t}>{ECHEANCE_LABEL[t]}</option>)}
          </select>
          <Input placeholder="Organisme (compagnie, centre…)" value={form.organisme} onChange={(e) => setForm({ ...form, organisme: e.target.value })} />
          <Input placeholder="N° de police / contrat" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          <div><label className="mb-1 block text-xs text-content-muted">Début</label><Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} /></div>
          <div><label className="mb-1 block text-xs text-content-muted">Expiration</label><Input type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} /></div>
          <Input type="number" step="1" placeholder="Montant / prime (DZD)" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleDoc(f); e.target.value = ''; }} />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} loading={uploading}><Paperclip className="size-4" /> {form.document_url ? 'Contrat joint ✓' : 'Joindre le contrat / la police'}</Button>
            {form.document_url && <button type="button" onClick={() => setForm((f) => ({ ...f, document_url: '' }))} className="text-xs text-status-stuck hover:underline">retirer</button>}
          </div>
          <div className="flex gap-2">
            {editId && <Button variant="secondary" onClick={reset}>Annuler</Button>}
            <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending}><Plus /> {editId ? 'Enregistrer' : "Ajouter l'échéance"}</Button>
          </div>
        </div>
      </div>
      {isLoading ? <Skeleton className="h-48 w-full" /> : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Type</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Organisme</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">N° police</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Expiration</th>
              <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Montant</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">État</th>
              <th className="px-4 py-2.5 text-center font-semibold text-content-secondary">Contrat</th>
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {data.map((ech) => {
                const s = statutEcheance(ech.date_fin);
                return (
                  <tr key={ech.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                    <td className="px-4 py-2.5 font-medium text-content-primary">{ECHEANCE_LABEL[ech.type]}</td>
                    <td className="px-4 py-2.5 text-content-secondary">{ech.organisme ?? '—'}</td>
                    <td className="px-4 py-2.5 text-content-secondary">{ech.reference ?? '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums text-content-secondary">{ech.date_fin ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{ech.montant != null ? formatDZD(ech.montant) : '—'}</td>
                    <td className="px-4 py-2.5">{s && <StatusBadge status={s.status} label={s.label} />}</td>
                    <td className="px-4 py-2.5 text-center">{ech.document_url ? <a href={ech.document_url} target="_blank" rel="noreferrer" className="text-brand-primary hover:opacity-70" title="Ouvrir le contrat"><ExternalLink className="inline size-4" /></a> : <span className="text-content-muted">—</span>}</td>
                    <td className="px-4 py-2.5 text-right"><RowActions onEdit={() => startEdit(ech)} onDelete={() => { if (window.confirm('Supprimer cette échéance ?')) delMut.mutate(ech.id); }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <EmptyState message="Aucune échéance. Ajoutez l'assurance, le contrôle technique et la vignette pour activer les rappels." />}
    </div>
  );
}

function AffectationsTab({ enginId }: { enginId: string }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ chantier_id: '', date_debut: today(), date_fin: '' });
  const { data, isLoading } = useQuery({ queryKey: ['affectations', enginId], queryFn: () => listAffectations(enginId) });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers', 'actifs'], queryFn: () => listChantiers() });
  const nomChantier = (cid: string) => chantiers?.items.find((c) => c.id === cid) ? `${chantiers.items.find((c) => c.id === cid)!.code} · ${chantiers.items.find((c) => c.id === cid)!.nom}` : '—';

  const addMut = useMutation({
    mutationFn: () => addAffectation(enginId, { chantier_id: form.chantier_id, date_debut: form.date_debut || null, date_fin: form.date_fin || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affectations', enginId] });
      queryClient.invalidateQueries({ queryKey: ['engin', enginId] });
      queryClient.invalidateQueries({ queryKey: ['engins'] });
      toast.success('Engin affecté au chantier.');
      setForm((f) => ({ ...f, date_fin: '' }));
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-3 text-base font-bold text-content-primary">Affecter à un chantier</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <select value={form.chantier_id} onChange={(e) => setForm({ ...form, chantier_id: e.target.value })} className={selectCls}>
            <option value="">— Sélectionner un chantier —</option>
            {chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
          </select>
          <Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} />
          <Input type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} />
        </div>
        <div className="mt-3 flex justify-end"><Button onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!form.chantier_id}><Plus /> Affecter</Button></div>
      </div>
      {isLoading ? <Skeleton className="h-48 w-full" /> : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Chantier</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Début</th>
              <th className="px-4 py-2.5 font-semibold text-content-secondary">Fin</th>
            </tr></thead>
            <tbody>
              {data.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2.5 font-medium text-content-primary">{nomChantier(a.chantier_id)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{a.date_debut ?? '—'}</td>
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{a.date_fin ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState message="Aucune affectation. Affectez l'engin à un chantier." />}
    </div>
  );
}
